import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, CalendarDays, Flag, CheckSquare } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import TaskSlideOver from './TaskSlideOver';
import EmptyState from './EmptyState';
import { dateKey, buildMonthGrid, stylesForTarea, DOW_LABELS, MONTH_LABELS } from './utils/helpers';
import { AsignadosStack } from './utils/displays';

// ----- Pill cross-proyecto (no draggable) -----

const TareaPill = ({ tarea, onClick }) => {
  const { bg, txt } = stylesForTarea(tarea);
  const color = tarea.proyecto_color || '#06b6d4';
  const proyectoNombre = tarea.proyecto_nombre || tarea.proyecto_slug || '';
  const asignados = tarea.asignados || [];
  const esSubtarea = tarea.es_subtarea === true;
  // En subtareas el tooltip incluye la tarea padre (CDQ-12) para dejar claro
  // que el click abre la tarea, no la subtarea.
  const nombres = asignados.map((a) => a.nombre).join(', ');
  const tooltip = esSubtarea
    ? `${proyectoNombre} · ${tarea.tarea_codigo || ''} · ${tarea.titulo}${nombres ? ` · ${nombres}` : ''}`
    : `${proyectoNombre} · ${tarea.titulo}${nombres ? ` · ${nombres}` : ''}`;

  return (
    <button
      onClick={() => onClick?.(tarea)}
      title={tooltip}
      className={`group w-full text-left relative flex items-center gap-1 px-1.5 py-1 rounded text-[11px] hover:ring-1 hover:ring-slate-900/10 transition-all ${bg}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      {esSubtarea && (
        <CheckSquare size={10} strokeWidth={2} className="text-slate-400 flex-shrink-0" />
      )}
      {tarea.prioridad === 'urgente' && (
        <Flag size={9} strokeWidth={2.5} className="text-rose-500 flex-shrink-0" style={{ fill: 'currentColor' }} />
      )}
      {tarea.prioridad === 'alta' && (
        <Flag size={9} strokeWidth={2.5} className="text-orange-500 flex-shrink-0" style={{ fill: 'currentColor' }} />
      )}
      <span className={`flex-1 truncate ${esSubtarea ? `${txt} italic` : txt}`}>{tarea.titulo}</span>
      {asignados.length > 0 && (
        <span className="flex-shrink-0">
          <AsignadosStack asignados={asignados} color={color} size={16} max={1} />
        </span>
      )}
    </button>
  );
};

// ----- Celda -----

const VISIBLE_LIMIT = 3;

const DayCell = ({ cell, cellIndex, tareas, onCardClick }) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);
  // Filas 5 y 6 (índices 28-41): abrir popover hacia arriba para no pisar fila siguiente.
  const openUpward = cellIndex >= 28;

  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [overflowOpen]);

  const visibles = tareas.slice(0, VISIBLE_LIMIT);
  const ocultas = tareas.slice(VISIBLE_LIMIT);

  return (
    <div
      className={`relative border-r border-b border-slate-200 p-2 flex flex-col ${
        !cell.inMonth ? 'bg-slate-100/70' : cell.isToday ? 'bg-cyan-50/60' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span
          className={`text-[11px] tabular-nums ${
            cell.isToday
              ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white font-semibold'
              : cell.inMonth
              ? 'text-slate-500 font-medium'
              : 'text-slate-300'
          }`}
        >
          {cell.date.getDate()}
        </span>
      </div>

      <div className="space-y-1">
        {visibles.map((t) => (
          <TareaPill key={t.id} tarea={t} onClick={onCardClick} />
        ))}

        {ocultas.length > 0 && (
          <div className="relative" ref={overflowRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setOverflowOpen((v) => !v); }}
              className="w-full text-left px-1.5 py-0.5 text-[10px] font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
            >
              + {ocultas.length} más
            </button>
            <AnimatePresence>
              {overflowOpen && (
                <motion.div
                  initial={{ opacity: 0, y: openUpward ? 4 : -4, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: openUpward ? 4 : -4, scale: 0.97 }}
                  transition={{ duration: 0.12 }}
                  className={`absolute left-0 w-64 bg-white rounded-lg shadow-2xl ring-1 ring-slate-200 overflow-hidden z-20 ${
                    openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
                  }`}
                >
                  <div className="px-3 py-2 border-b border-slate-200/70">
                    <span className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase">
                      {cell.date.toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  <div className="p-1.5 space-y-1 max-h-72 overflow-y-auto">
                    {tareas.map((t) => (
                      <TareaPill
                        key={t.id}
                        tarea={t}
                        onClick={(tarea) => { setOverflowOpen(false); onCardClick(tarea); }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

// ----- Vista principal -----

const CalendarioGlobalView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tareas, setTareas] = useState([]);
  const [proyectosCache, setProyectosCache] = useState({});
  const [loading, setLoading] = useState(true);

  const [current, setCurrent] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const [slideOverTareaId, setSlideOverTareaId] = useState(null);
  const [slideOverProyecto, setSlideOverProyecto] = useState(null);

  // Cargar datos
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tareasResp, proyectosResp] = await Promise.all([
        axiosInstance.get('/api/tareas', { params: { asignado: 'me' } }),
        axiosInstance.get('/api/proyectos'),
      ]);
      const colorBySlug = {};
      (proyectosResp.data || []).forEach((p) => { colorBySlug[p.slug] = p.color; });
      // Incluye filas de tarea y filas de subtarea (es_subtarea); ambas traen
      // fecha_vencimiento y proyecto_slug. Las subtareas ya traen proyecto_color
      // del backend, lo dejamos como fallback si el proyecto no está en mi lista.
      const enriquecidas = (tareasResp.data || [])
        .filter((t) => t.fecha_vencimiento)
        .map((t) => ({
          ...t,
          proyecto_color: colorBySlug[t.proyecto_slug] || t.proyecto_color || '#06b6d4',
        }));
      setTareas(enriquecidas);
    } catch (err) {
      console.error('Error cargando calendario:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Proyecto a demanda para slide-over
  const loadProyecto = useCallback(async (slug) => {
    if (proyectosCache[slug]) return proyectosCache[slug];
    try {
      const { data } = await axiosInstance.get(`/api/proyectos/${slug}`);
      setProyectosCache((prev) => ({ ...prev, [slug]: data }));
      return data;
    } catch (err) {
      return null;
    }
  }, [proyectosCache]);

  const { cells } = useMemo(() => buildMonthGrid(current.year, current.month), [current]);

  const tareasPorDia = useMemo(() => {
    const m = {};
    tareas.forEach((t) => {
      if (!m[t.fecha_vencimiento]) m[t.fecha_vencimiento] = [];
      m[t.fecha_vencimiento].push(t);
    });
    return m;
  }, [tareas]);

  // Click en una tarea: navegar al URL canónico del proyecto (mismo patrón
  // que MisTareas y GanttGlobal). Sale del calendario global pero garantiza
  // URLs compartibles y consistentes. El back del navegador regresa.
  //
  // Click en una fila de subtarea (es_subtarea): abrir el slide-over de la TAREA
  // PADRE, no de la subtarea. La subtarea no tiene `numero` propio, así que usamos
  // la ruta global por id del padre (tarea_id); el efecto de sincronización abre
  // la tarea (la trae con fetch si no está en mi lista, p.ej. cuando el padre no
  // tiene fecha y por eso no es una pill).
  const handleCardClick = useCallback((tarea) => {
    if (tarea.es_subtarea) {
      if (!tarea.tarea_id) return;
      navigate(`/proyectos/calendario/tareas/${tarea.tarea_id}`);
      return;
    }
    navigate(`/proyectos/${tarea.proyecto_slug}/tareas/${tarea.numero}`);
  }, [navigate]);

  const handleCloseSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    setSlideOverProyecto(null);
    navigate('/proyectos/calendario', { replace: false });
  }, [navigate]);

  // Sincronizar slide-over con URL. location.pathname permite back/forward;
  // si la tarea no esta en mi lista (link compartido, sin fecha asignada para
  // mostrarse en el calendario, etc), hacemos fetch directo.
  useEffect(() => {
    const match = location.pathname.match(/\/proyectos\/calendario\/tareas\/(\d+)/);
    if (!match) {
      setSlideOverTareaId(null);
      setSlideOverProyecto(null);
      return;
    }
    const id = parseInt(match[1], 10);
    if (tareas.length === 0 && loading) return; // todavia cargando
    const t = tareas.find((x) => x.id === id);
    if (t) {
      setSlideOverTareaId(id);
      loadProyecto(t.proyecto_slug).then((p) => { if (p) setSlideOverProyecto(p); });
      return;
    }
    axiosInstance.get(`/api/tareas/${id}`)
      .then(({ data }) => {
        if (!data) return;
        setSlideOverTareaId(id);
        loadProyecto(data.proyecto_slug).then((p) => { if (p) setSlideOverProyecto(p); });
      })
      .catch(() => {
        navigate('/proyectos/calendario', { replace: true });
      });
  }, [tareas, loading, location.pathname, loadProyecto, navigate]);

  // Navegacion mes
  const prevMonth = useCallback(() => setCurrent((c) => ({
    year: c.month === 0 ? c.year - 1 : c.year,
    month: c.month === 0 ? 11 : c.month - 1,
  })), []);
  const nextMonth = useCallback(() => setCurrent((c) => ({
    year: c.month === 11 ? c.year + 1 : c.year,
    month: c.month === 11 ? 0 : c.month + 1,
  })), []);
  const goToday = useCallback(() => {
    const d = new Date();
    setCurrent({ year: d.getFullYear(), month: d.getMonth() });
  }, []);

  // Atajos: ←/→ navega meses, T va a hoy. Desactivados con slide-over abierto.
  useEffect(() => {
    if (slideOverTareaId) return;
    const onKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevMonth(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); nextMonth(); }
      else if (e.key.toLowerCase() === 't') { e.preventDefault(); goToday(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [slideOverTareaId, prevMonth, nextMonth, goToday]);

  return (
    <div className="max-w-[1400px] mx-auto px-8 pt-8 pb-12">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Mi calendario</h1>
          <span className="text-sm text-slate-400 tabular-nums">
            {tareas.length} con fecha
          </span>
        </div>
        <p className="text-sm text-slate-500">
          Tus tareas con fecha en una grilla mensual. El punto de color identifica el proyecto.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={prevMonth}
          title="Mes anterior (←)"
          className="p-1 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
        >
          <ChevronLeft size={14} />
        </button>
        <h3 className="text-sm font-semibold tracking-tight text-slate-900 tabular-nums min-w-[120px] text-center">
          {MONTH_LABELS[current.month]} {current.year}
        </h3>
        <button
          onClick={nextMonth}
          title="Mes siguiente (→)"
          className="p-1 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
        >
          <ChevronRight size={14} />
        </button>
        <button
          onClick={goToday}
          title="Ir a hoy (T)"
          className="ml-2 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
        >
          Hoy
        </button>
        <div className="flex-1" />
        <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-slate-400 tracking-wide">
          <kbd className="px-1 py-px rounded bg-slate-100 border border-slate-200 text-slate-500 font-mono text-[9px]">←</kbd>
          <kbd className="px-1 py-px rounded bg-slate-100 border border-slate-200 text-slate-500 font-mono text-[9px]">→</kbd>
          <span className="ml-1">navegar</span>
          <span className="mx-1 text-slate-200">·</span>
          <kbd className="px-1 py-px rounded bg-slate-100 border border-slate-200 text-slate-500 font-mono text-[9px]">T</kbd>
          <span className="ml-1">hoy</span>
        </span>
      </div>

      {/* Calendario */}
      {loading ? (
        <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-100/60">
            {DOW_LABELS.map((d) => (
              <div
                key={d}
                className="px-2 py-1.5 text-[10px] font-semibold tracking-wider text-slate-500 uppercase text-center"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 grid-rows-6 min-h-[650px] skeleton">
            {[...Array(42)].map((_, i) => (
              <div key={i} className="border-r border-b border-slate-200 p-2">
                <div className="h-3 w-4 bg-slate-100 rounded mb-2" />
                {i % 5 === 0 && <div className="h-4 w-full bg-slate-100/70 rounded mb-1" />}
                {i % 7 === 2 && <div className="h-4 w-3/4 bg-slate-100/70 rounded" />}
              </div>
            ))}
          </div>
        </div>
      ) : tareas.length === 0 ? (
        <div className="border border-slate-200 rounded-lg bg-white p-12">
          <EmptyState
            icon={CalendarDays}
            title="Nada agendado todavía"
            description="Cuando le pongas fecha a una tarea asignada a ti, va a aparecer acá en el día correspondiente."
            variant="centered"
          />
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-100/60">
            {DOW_LABELS.map((d) => (
              <div
                key={d}
                className="px-2 py-1.5 text-[10px] font-semibold tracking-wider text-slate-500 uppercase text-center"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 grid-rows-6 min-h-[650px]">
            {cells.map((cell, i) => {
              const tareasCelda = tareasPorDia[dateKey(cell.date)] || [];
              return (
                <DayCell
                  key={i}
                  cell={cell}
                  cellIndex={i}
                  tareas={tareasCelda}
                  onCardClick={handleCardClick}
                />
              );
            })}
          </div>
        </div>
      )}

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={slideOverProyecto}
        onClose={handleCloseSlideOver}
        onUpdate={(tareaFresca) => {
          // Preservar proyecto_color (campo derivado que no viene del endpoint).
          setTareas((prev) => prev.map((t) =>
            t.id === tareaFresca.id
              ? { ...t, ...tareaFresca, proyecto_color: t.proyecto_color }
              : t
          ));
        }}
        onArchive={() => {
          // Filtrado local en vez de fetchAll: archivar una tarea no justifica
          // recargar /proyectos + /tareas.
          setTareas((prev) => prev.filter((t) => t.id !== slideOverTareaId));
          handleCloseSlideOver();
        }}
        onDuplicate={(nueva) => { fetchAll(); setSlideOverTareaId(nueva.id); }}
      />
    </div>
  );
};

export default CalendarioGlobalView;
